import { useCallback, useMemo } from "react";
import _ from "lodash";

import { URLS } from "config/urls";
import {
  CompareArrowsIcon,
  DeleteOutlineOutlinedIcon,
  ModeEditOutlineOutlinedIcon,
  MoreVertIcon,
} from "icons";
import { handleInvalidRequest } from "utils/sdk";
import { reverse } from "utils/urls";

import ButtonDropdown from "components/ButtonDropdown";
import Link from "components/Link";
import { MenuItem } from "components/Menu";
import toast from "components/Toast";

import { dealTableDelete, dealTableUpdate } from "entities/Deal/sdk";
import DeleteButton from "entities/TableStorage/components/DeleteButton";
import TableRenameDialogMenuItem from "entities/TableStorage/components/TableRenameDialogMenuItem";
import {
  tableDelete,
  tableUpdate,
  TableUpdateData,
} from "entities/TableStorage/sdk";

export interface IActionButtons {
  tableId: number;
  initialValue: string;
  onImportDelete?: () => void;
  onImportUpdate?: () => void;
  updateCallback: () => void;
  deleteCallback: () => void;
  dealId?: number;
}

const ActionButtons = ({
  tableId,
  initialValue,
  onImportUpdate,
  onImportDelete,
  dealId,
  updateCallback,
  deleteCallback,
}: IActionButtons) => {
  const compareLink = useMemo(() => {
    if (!_.isNil(dealId)) {
      return reverse(URLS.DEAL_TABLES_COMPARE, { dealId, tableId });
    }

    return reverse(URLS.TABLES_COMPARE_WITH_INITIAL, { tableId });
  }, [tableId, dealId]);

  const handleTableUpdate = useCallback(
    ({ values }: { values: TableUpdateData }) => {
      if (!_.isNil(dealId)) {
        dealTableUpdate({ tableId, dealId, data: values })
          .then(() => {
            toast.successMessage("Table updated successfully!");
            updateCallback();
          })
          .catch(handleInvalidRequest);
      } else {
        tableUpdate({ tableId, data: values })
          .then(() => {
            toast.successMessage("Table updated successfully!");
            updateCallback();
          })
          .catch(handleInvalidRequest);
      }
    },
    [tableId, dealId, updateCallback]
  );

  const handleTableDelete = useCallback(() => {
    if (!_.isNil(dealId)) {
      dealTableDelete({ tableId, dealId })
        .then(() => {
          toast.successMessage("Table deleted successfully");
          deleteCallback();
        })
        .catch(handleInvalidRequest);
    } else {
      tableDelete({ tableId })
        .then(() => {
          toast.successMessage("Table deleted successfully!");
          deleteCallback();
        })
        .catch(handleInvalidRequest);
    }
  }, [dealId, tableId, deleteCallback]);

  return (
    <ButtonDropdown
      variant="text"
      size="small"
      label={<MoreVertIcon />}
      width="auto"
      keepMenuMounted
    >
      <TableRenameDialogMenuItem
        initialValue={initialValue}
        onUpdate={handleTableUpdate}
      />
      {!_.isNil(onImportUpdate) && (
        <MenuItem onClick={onImportUpdate}>
          <ModeEditOutlineOutlinedIcon
            sx={{ marginRight: 1, height: "24px", width: "24px" }}
          />{" "}
          Modify cell range
        </MenuItem>
      )}
      {!_.isNil(onImportDelete) && (
        <MenuItem onClick={onImportDelete}>
          <DeleteOutlineOutlinedIcon
            sx={{ marginRight: 1, height: "24px", width: "24px" }}
          />{" "}
          Delete cell range
        </MenuItem>
      )}
      <DeleteButton onDelete={handleTableDelete} buttonType="menuItem" />
      <Link to={compareLink}>
        <MenuItem>
          <CompareArrowsIcon
            sx={{ marginRight: 1, height: "24px", width: "24px" }}
          />
          Compare
        </MenuItem>
      </Link>
    </ButtonDropdown>
  );
};

export default ActionButtons;
